import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type MatchSourceKind = 'upload' | 'feed';

export type MatchSource = {
  kind: MatchSourceKind;
  uploadId?: string;
  fileName?: string;
  uploadKind?: string;
  schoolYear?: string;
  nameColumns?: string[];
};

export type MatchRecord = {
  source: MatchSource;
  stateStudentId?: string;
  districtStudentId?: string;
  stateAliases?: string[];
  districtAliases?: string[];
  firstName?: string;
  lastName?: string;
  dob?: string;
};

export type MatchAnswerKind = 'same_student' | 'keep_separate';
export type TriggeringField = 'name' | 'dob' | 'id' | 'missing-id';

export type MatchAnswer = { kind: MatchAnswerKind; decidedAt: string };

export type PossibleMatch = {
  id: string;
  type: string;
  triggeringField: TriggeringField;
  records: MatchRecord[];
  answer: MatchAnswer | null;
};

export type SetAsideFile = {
  uploadId: string;
  fileName: string;
  against: 'file' | 'feed';
  case: 'file_vs_file' | 'access_ids_wrong' | 'sis_local_ids';
  matched: number;
  total: number;
};

export type MatchesState = {
  toReview: number;
  reviewed: number;
  heldOutStudents: number;
  matches: PossibleMatch[];
  setAside: SetAsideFile[];
};

export const EMPTY_MATCHES: MatchesState = { toReview: 0, reviewed: 0, heldOutStudents: 0, matches: [], setAside: [] };

const ANSWER_KINDS = new Set<string>(['same_student', 'keep_separate']);
const FIELDS = new Set<string>(['name', 'dob', 'id', 'missing-id']);
const CASES = new Set<string>(['file_vs_file', 'access_ids_wrong', 'sis_local_ids']);

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(str).filter((id) => id !== '') : [];
}

function asRecord(raw: unknown): MatchRecord {
  const r = (raw ?? {}) as Record<string, unknown>;
  const source = (r.source ?? {}) as Record<string, unknown>;
  const nameColumns = strList(source.nameColumns);
  return {
    source: {
      kind: source.kind === 'feed' ? 'feed' : 'upload',
      uploadId: str(source.uploadId) || undefined,
      fileName: str(source.fileName) || undefined,
      uploadKind: str(source.uploadKind) || undefined,
      schoolYear: str(source.schoolYear) || undefined,
      nameColumns: nameColumns.length > 0 ? nameColumns : undefined,
    },
    stateStudentId: str(r.stateStudentId) || undefined,
    districtStudentId: str(r.districtStudentId) || undefined,
    stateAliases: strList(r.stateAliases),
    districtAliases: strList(r.districtAliases),
    firstName: str(r.firstName) || undefined,
    lastName: str(r.lastName) || undefined,
    dob: str(r.dob) || undefined,
  };
}

function asAnswer(raw: unknown): MatchAnswer | null {
  const a = raw as Record<string, unknown> | null | undefined;
  if (!a || !ANSWER_KINDS.has(str(a.kind))) return null;
  return { kind: str(a.kind) as MatchAnswerKind, decidedAt: str(a.decidedAt) };
}

function asMatch(raw: unknown): PossibleMatch | null {
  const m = (raw ?? {}) as Record<string, unknown>;
  const id = str(m.id);
  if (id === '') return null;
  const field = str(m.triggeringField);
  return {
    id,
    type: str(m.type),
    triggeringField: (FIELDS.has(field) ? field : 'id') as TriggeringField,
    records: Array.isArray(m.records) ? m.records.map(asRecord) : [],
    answer: asAnswer(m.answer),
  };
}

function asSetAside(raw: unknown): SetAsideFile | null {
  const s = (raw ?? {}) as Record<string, unknown>;
  const uploadId = str(s.uploadId);
  if (uploadId === '') return null;
  const kase = str(s.case);
  return {
    uploadId,
    fileName: str(s.fileName),
    against: s.against === 'feed' ? 'feed' : 'file',
    case: (CASES.has(kase) ? kase : 'file_vs_file') as SetAsideFile['case'],
    matched: num(s.matched),
    total: num(s.total),
  };
}

export function parseMatchesState(body: unknown): MatchesState {
  const b = (body ?? {}) as Record<string, unknown>;
  return {
    toReview: num(b.toReview),
    reviewed: num(b.reviewed),
    heldOutStudents: num(b.heldOutStudents),
    matches: Array.isArray(b.matches) ? b.matches.map(asMatch).filter((m): m is PossibleMatch => m !== null) : [],
    setAside: Array.isArray(b.setAside) ? b.setAside.map(asSetAside).filter((s): s is SetAsideFile => s !== null) : [],
  };
}

export function needsAttention(state: MatchesState): boolean {
  return state.toReview > 0;
}

export class MatchesRefreshError extends Error {}

async function call(path: string, init: RequestInit = {}): Promise<MatchesState> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}${path}`, {
    ...init,
    headers: { ...authHeaders(token), ...(init.headers as Record<string, string> | undefined) },
  });
  if (!response.ok) {
    throw new Error(`matches ${init.method ?? 'GET'} failed: ${response.status}`);
  }
  if (response.status === 202) {
    try {
      return await call('/matches');
    } catch (e) {
      throw new MatchesRefreshError(`matches refresh after ${init.method ?? 'GET'} failed: ${String(e)}`);
    }
  }
  return parseMatchesState(await response.json());
}

export function fetchMatches(): Promise<MatchesState> {
  return call('/matches');
}

export function answerMatch(id: string, kind: MatchAnswerKind): Promise<MatchesState> {
  return call(`/matches/${encodeURIComponent(id)}/answer`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind }),
  });
}

export function clearMatchAnswer(id: string): Promise<MatchesState> {
  return call(`/matches/${encodeURIComponent(id)}/answer`, { method: 'DELETE' });
}
